import { Injectable } from '@angular/core';

import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';

import { LoadingIndicator, LoadingType, Where } from '@osos/shared/types';
import { EMPTY, Observable, of } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { ApiUtilsService, CollectionDataService } from '../../services';

import { CollectionPresenterModel } from '@osos/shared/types';

import { DataCollections } from '@osos/shared/api';
import { LovFactoryService } from '../../factories/lov-factory/lov-factory.service';
import { CoreCollectionActions } from './core-collection.actions';

export interface CollectionCoreStateModel {
  presentedLookup: { [key: string]: CollectionPresenterModel };
  loading: LoadingIndicator;
  /**
   * temporary state to store lookups and select them once
   */
  transitoryLookups?: CollectionPresenterModel[];
}

@State<CollectionCoreStateModel>({
  name: 'coreLookups',
  defaults: {
    presentedLookup: {},
    transitoryLookups: [],
    loading: new LoadingIndicator(),
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class CoreCollectionState {
  private requestsInProgress = {};

  constructor(
    private apiHelper: ApiUtilsService,
    private lookupsService: CollectionDataService,
    private lookupFactoryService: LovFactoryService,
  ) {}
  // ignore entityId until we have clear routing
  private static getKey(entityId: string, lookupId: string): string {
    return `${''}${lookupId}`;
  }

  private getKey = CoreCollectionState.getKey;

  // --------------------------------- SELECTORS ------------------------------

  static presentedLookup(entityId: string, lookupId: string) {
    return createSelector(
      [CoreCollectionState],
      (state: CollectionCoreStateModel) =>
        state.presentedLookup[CoreCollectionState.getKey(entityId, lookupId)],
    );
  }

  static loading(type: LoadingType) {
    return createSelector(
      [CoreCollectionState],
      (state: CollectionCoreStateModel) => state.loading[type],
    );
  }

  /**
   * select transitory lookups.
   * it's valid to be read once after trigger TransitoryFetchLookups action
   */
  @Selector([CoreCollectionState])
  static transitoryLookups(state: CollectionCoreStateModel) {
    return state.transitoryLookups;
  }

  // --------------------------------- ACTIONS --------------------------------

  @Action(CoreCollectionActions.FetchLookup)
  fetchLookup(
    { setState, getState }: StateContext<CollectionCoreStateModel>,
    { lookupId }: CoreCollectionActions.FetchLookup,
  ): Observable<any> {
    const lookup = getState().presentedLookup[lookupId];
    if (lookup) {
      return of(lookup);
    }
    if (this.requestsInProgress[lookupId]) {
      return EMPTY;
    }
    this.requestsInProgress[lookupId] = true;

    setState(
      patch<CollectionCoreStateModel>({
        loading: patch<LoadingIndicator>({
          item: true,
        }),
      }),
    );

    return this.lookupsService.findByIdWithoutEntity({ id: lookupId }).pipe(
      tap((res) => {
        const presenter = this.lookupFactoryService.createPresenterModel(res);
        setState(
          patch<CollectionCoreStateModel>({
            presentedLookup: patch({
              [this.getKey(presenter.dataEntity, lookupId)]: presenter,
            }),
            loading: patch<LoadingIndicator>({
              item: false,
            }),
          }),
        );
      }),
      finalize(() => {
        this.requestsInProgress[lookupId] = false;

        setState(
          patch<CollectionCoreStateModel>({
            loading: patch<LoadingIndicator>({
              item: false,
            }),
          }),
        );
      }),
    );
  }

  /**
   * fetch lookups then you can read them from transitoryLookups selector
   */
  @Action(
    CoreCollectionActions.TransitoryFetchLookups,
    // Check with amer why we cancel the previous fetches
    //    {
    //   cancelUncompleted: true,
    // }
  )
  transitoryFetchLookups(
    { setState }: StateContext<CollectionCoreStateModel>,
    { payload }: CoreCollectionActions.TransitoryFetchLookups,
  ): Observable<DataCollections[]> {
    const where: Where<DataCollections> = {
      id: payload.lookupIds?.length ? { inq: payload.lookupIds } : undefined,
      or: this.apiHelper.prepareSearch(payload.searchTerm, [
        'name.en',
        'name.ar',
        'description.en',
        'description.ar',
      ]),
      dataEntity: payload.entityIds?.length
        ? { inq: payload.entityIds }
        : undefined,
    };

    const filter = this.apiHelper.prepareFilter<DataCollections>({
      where,
      skip: payload.skip ?? 0,
      limit: payload.limit ?? 20,
    });

    return this.lookupsService.findAll(filter).pipe(
      tap((res) => {
        setState(
          patch<CollectionCoreStateModel>({
            transitoryLookups: res.map((x) =>
              this.lookupFactoryService.createPresenterModel(x),
            ),
          }),
        );
      }),
    );
  }

  @Action(CoreCollectionActions.ClearLookup)
  clearLookup(
    { setState }: StateContext<CollectionCoreStateModel>,
    { entityId, lookupId }: CoreCollectionActions.ClearLookup,
  ): void {
    setState(
      patch<CollectionCoreStateModel>({
        presentedLookup: patch({
          [this.getKey(entityId, lookupId)]: null,
        }),
      }),
    );
  }

  @Action(CoreCollectionActions.UpdateLookup)
  updateLookup(
    { setState }: StateContext<CollectionCoreStateModel>,
    { entityId, payload }: CoreCollectionActions.UpdateLookup,
  ): void {
    setState(
      patch<CollectionCoreStateModel>({
        presentedLookup: patch({
          [this.getKey(entityId, payload.id)]: payload,
        }),
      }),
    );
  }
}
